import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { ContextKindSelector } from '@gonfalon/contexts/components/ContextKindSelector';
import { unifiedContextKindDisplay } from '@gonfalon/dogfood-flags/dogfood-flags';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { toContextKinds, toDataSourcesMetricDetails, toHref } from '@gonfalon/navigator/navigators';
import type { RandomizationUnitRep } from '@gonfalon/openapi/types';
import { getMetricDataSourceByKeyQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { MultiSelectWithSearch } from '@gonfalon/ui/multi-select-with-search';
import { Alert } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import type { MetricForm, RandomizationUnitOption } from '../formUtils';
import { useProjectExperimentationSettings } from '../useProjectExperimentationSettings';

import styles from './MetricRandomizationUnits.module.css';

type RandomizationUnit = RandomizationUnitRep;

interface MetricRandomizationUnitsProps {
  onSelect?: (selection: string[], options: RandomizationUnitOption[]) => void;
  initialValues?: string[];
  dataSourceKey?: string;
}

const useMetricRandomizationUnits = ({ dataSourceKey }: { dataSourceKey?: string }) => {
  const projectKey = useProjectKey();
  const isExternalDataSource = typeof dataSourceKey === 'string' && !isLDHosted(dataSourceKey);

  const {
    data: settings,
    isPending,
    isError,
  } = useProjectExperimentationSettings({
    projectKey,
  });
  const {
    data: dataSource,
    isPending: isPendingDataSource,
    isError: isErrorDataSource,
  } = useQuery({
    ...getMetricDataSourceByKeyQuery({ projKey: projectKey, dataSourceKey: dataSourceKey ?? '' }),
    enabled: isExternalDataSource,
  });

  const dataSourceContextKeys = useMemo(() => Object.keys(dataSource?.columnMappings?.contexts ?? {}), [dataSource]);

  return {
    data: isExternalDataSource
      ? settings?.randomizationUnits?.filter(
          (ru) => ru.randomizationUnit && dataSourceContextKeys.includes(ru.randomizationUnit),
        )
      : settings?.randomizationUnits,
    isPending: isExternalDataSource ? isPendingDataSource || isPending : isPending,
    isError: isExternalDataSource ? isErrorDataSource || isError : isError,
  };
};

export function MetricRandomizationUnits({ onSelect, initialValues, dataSourceKey }: MetricRandomizationUnitsProps) {
  const { data: randomizationUnits, isPending, isError } = useMetricRandomizationUnits({ dataSourceKey });
  const useUnifiedSelector = unifiedContextKindDisplay();
  // Detect edit mode by capturing the form's initial randomizationUnits at mount.
  // In create mode the default is []; in edit mode it's populated from the persisted
  // metric. We snapshot in a ref because RHF's formState.defaultValues can be mutated
  // by subsequent setValue calls (especially when useForm is driven by a `values` prop),
  // which would otherwise let this flag flip after the auto-default effect fires.
  const { formState, setError, clearErrors } = useFormContext<MetricForm>();
  const initialRandomizationUnitsRef = useRef(formState.defaultValues?.randomizationUnits);
  const isEditingExistingMetric = Boolean(initialRandomizationUnitsRef.current?.length);
  const prevDataSourceKeyRef = useRef<string | undefined>(dataSourceKey);

  const options: RandomizationUnitOption[] = useMemo(
    () => randomizationUnits?.map(toOption) ?? [],
    [randomizationUnits],
  );
  const defaultValues = useMemo(
    () =>
      initialValues && initialValues.length > 0
        ? initialValues
        : [options?.find((o) => o.default)?.value ?? options[0]?.value],
    [initialValues, options],
  );

  // Switching from an LD-hosted to an external (warehouse) data source always resets
  // the unit to the first available one for that source (handled in the effect below),
  // even when editing or cloning a metric that already had a unit set — a warehouse
  // source only supports its mapped context kinds, so the previous unit likely doesn't
  // apply. The data source is immutable in the edit modal, so in practice this only
  // fires in the create/clone flow.
  const prevDataSourceKey = prevDataSourceKeyRef.current;
  const switchedFromLdToExternalDataSource =
    prevDataSourceKey !== dataSourceKey &&
    isLDHosted(prevDataSourceKey) &&
    typeof dataSourceKey === 'string' &&
    !isLDHosted(dataSourceKey);

  // Orphaned state: when editing an existing metric whose persisted randomization
  // unit no longer exists among the available options (it was deleted from the
  // project's experimentation settings). The data source is immutable in edit mode,
  // so a missing option means deletion rather than an incompatible data source.
  // We resolve the display name from the snapshot of the form's initial value, which
  // carries the persisted unit's label even though it's absent from `options`.
  // We require a settled fetch (`!isPending && !isError`) rather than non-empty
  // options: if every unit was deleted the option list is empty, but the persisted
  // value is still orphaned and must be flagged. We also skip it while a switch to an
  // external data source is about to auto-select a new default, so the error doesn't
  // flash for the frame between the data source change and the reset.
  const willAutoSwitchToExternalDefault = switchedFromLdToExternalDataSource && options.length > 0;
  const orphanedValue =
    isEditingExistingMetric && !isPending && !isError && !willAutoSwitchToExternalDefault
      ? initialValues?.find((value) => !options.some((option) => option.value === value))
      : undefined;
  const orphanedUnitName = orphanedValue
    ? (initialRandomizationUnitsRef.current?.find((unit) => unit?.value === orphanedValue)?.label ?? orphanedValue)
    : undefined;
  const isOrphaned = Boolean(orphanedUnitName);

  // When an external (non-LD) data source is selected, the unit isn't "gone" — it
  // just isn't mapped for that data source — so the copy and recovery link differ.
  const isExternalDataSource = typeof dataSourceKey === 'string' && !isLDHosted(dataSourceKey);

  useEffect(() => {
    if (isOrphaned) {
      setError('randomizationUnits', {
        type: 'manual',
        message: isExternalDataSource
          ? `Randomization unit '${orphanedUnitName}' is not configured for this data source. Select a different one.`
          : `Randomization unit '${orphanedUnitName}' no longer exists. Select a different one.`,
      });
    } else {
      clearErrors('randomizationUnits');
    }
  }, [isOrphaned, orphanedUnitName, isExternalDataSource, setError, clearErrors]);

  useEffect(() => {
    // Leave a persisted unit alone (edit) or a seeded unit in place to be validated
    // (clone); only the create flow — or a switch to an external data source —
    // auto-defaults a selection.
    if (isEditingExistingMetric && !switchedFromLdToExternalDataSource) {
      return;
    }

    const dataSourceKeyChanged = prevDataSourceKeyRef.current !== dataSourceKey;
    // There are current selections that are not compatible with the newly selected data source
    const incompatibleSelection =
      Array.isArray(initialValues) && initialValues.some((val) => !options.some((option) => option?.value === val));
    // There are no selections at all (eg. no defaultValue passed to the form)
    const noSelectedValues = !initialValues?.length && options.length;
    // If any of the previous conditions are true, we should update the form value to the correct default for the
    // selected data source
    const shouldUpdateFormValue = dataSourceKeyChanged || incompatibleSelection || noSelectedValues;

    if (options.length && shouldUpdateFormValue) {
      const firstOption = options[0]?.value;
      const defaultOption = options.find((o) => o.default)?.value;

      onSelect?.([defaultOption || firstOption], options);
    }

    if (options.length) {
      prevDataSourceKeyRef.current = dataSourceKey;
    }
  }, [dataSourceKey, options, initialValues, onSelect, isEditingExistingMetric, switchedFromLdToExternalDataSource]);

  if (isError) {
    return <Alert status="error">Failed to load randomization units</Alert>;
  }

  if (useUnifiedSelector) {
    return (
      <div data-test-id="metric-randomization-units">
        <ContextKindSelector
          className={styles.selector}
          key={dataSourceKey}
          contextKinds={[
            ...options.map((option) => ({ key: option.value, name: option.label })),
            ...(orphanedValue ? [{ key: orphanedValue, name: orphanedUnitName ?? orphanedValue }] : []),
          ]}
          selectionMode="multiple"
          isSearchable
          shortenLabels
          disallowEmptySelection
          isDisabled={isPending}
          isInvalid={isOrphaned}
          selectedKeys={isPending ? [] : defaultValues.filter((value): value is string => typeof value === 'string')}
          onSelectionChange={(keys) => {
            if (keys.length > 0) {
              onSelect?.(keys, options);
            }
          }}
          placeholder="Search for units"
          aria-label="Randomization units"
        />
      </div>
    );
  }

  if (isPending) {
    return <MultiSelectWithSearch ariaLabel="Randomization Unit Multi Select" options={[]} isDisabled />;
  }

  return (
    <div data-test-id="metric-randomization-units">
      <MultiSelectWithSearch
        key={dataSourceKey}
        onSelect={onSelect && ((s: string[]) => onSelect(s, options))}
        ariaLabel="Randomization Unit Multi Select"
        options={options}
        values={defaultValues}
        placeholder="Search for units"
        disallowEmptySelection
        shortenLabels
        isInvalid={isOrphaned}
        invalidValueLabel={orphanedUnitName}
      />
    </div>
  );
}

function toOption(r: RandomizationUnit): RandomizationUnitOption {
  const value = r.randomizationUnit ?? '';
  return {
    label: r._displayName ?? value,
    value,
    default: r.default,
  };
}

interface RandomizationUnitsErrorProps {
  /** The randomization unit form error message, e.g. errors.randomizationUnits?.message. */
  message?: string;
  /** The currently selected data source key, used to pick the relevant recovery link. */
  dataSourceKey?: string;
}

/**
 * Renders the randomization unit form error (set above when a metric's selected
 * unit isn't valid) along with a recovery link. Co-located here so both the simple
 * (MetricDefinitionFormField) and ratio (RatioMetricSection) metric definition
 * layouts render the same message and link.
 *
 * For an external (non-LD) data source the unit isn't deleted — it just isn't
 * mapped for that data source — so we link to the data source configuration.
 * Otherwise we link to the project's context kind settings.
 *
 * The error is rendered here rather than via FieldGroup's `errorMessage` on
 * purpose: FieldGroup pushes its invalid state into every descendant Select's
 * context, which would put a red border on all the definition pickers. We only
 * want the randomization unit selector to show the invalid state (handled by its
 * own `isInvalid`).
 */
export function RandomizationUnitsError({ message, dataSourceKey }: RandomizationUnitsErrorProps) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();

  if (!message) {
    return null;
  }

  return (
    <span className={styles.randomizationUnitsError} role="alert">
      {message}{' '}
      {dataSourceKey && !isLDHosted(dataSourceKey) ? (
        <NewTabLink
          href={toHref(toDataSourcesMetricDetails({ projectKey, dataSourceKey }))}
          text="Data source configuration"
        />
      ) : (
        <NewTabLink href={toHref(toContextKinds({ projectKey, environmentKey }))} text="Context kind settings" />
      )}
    </span>
  );
}
